import methodology from './methodology.json';

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const interval = values => ({ low: Math.min(...values), high: Math.max(...values) });
const possible = (rows, value, key) => {
  const row = rows.find(row => row.v === value);
  if (row) return [row[key]];
  // “No comparable year” / no order book explicitly means the factor is inapplicable.
  if (value === 'notApplicable') return [key === 'm' ? 0 : 1];
  return rows.map(row => row[key]);
};
const lerp = (points, x) => {
  if (x <= points[0][0]) return points[0][1];
  for (let i = 1; i < points.length; i += 1) {
    if (x <= points[i][0]) {
      const [x0, y0] = points[i - 1];
      const [x1, y1] = points[i];
      return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
    }
  }
  return points[points.length - 1][1];
};
const known = n => typeof n === 'number' && Number.isFinite(n);

/** Original salary tiers, size interpolation and coefficients, kept unchanged. */
export const marketSalary = f =>
  f < 300000 ? 35000 : f < 1000000 ? 50000 : f < 5000000 ? 75000 : f < 20000000 ? 120000 : 180000;
export const sizeFactor = f =>
  lerp(
    [
      [0, 0.8],
      [500000, 0.85],
      [1000000, 0.9],
      [3000000, 1],
      [10000000, 1.15],
      [25000000, 1.3],
      [50000000, 1.45],
    ],
    f
  );

/**
 * Pure original methodology, with explicit missing data and no discontinuous fallback.
 * Revenue reference and adjusted-profit valuation are independent results.
 * Unknown bounded qualitative factors widen the range; unknown money blocks its subtotal.
 */
export const calculateValuation = (input, profile) => {
  if (!profile || !known(input.turnover) || input.turnover <= 0) return null;
  const growthValues = [];
  possible(methodology.growth, input.g12, 'm').forEach(g12 => {
    possible(methodology.growth, input.g24, 'm').forEach(g24 => {
      let growth = 1 + (clamp(g12, -40, 50) / 100) * 0.3 + (clamp(g24, -40, 50) / 100) * 0.1;
      if (g12 > g24 + 8) growth *= 1.03;
      else if (g12 > 0 && g12 < g24 - 8) growth *= 0.97;
      growthValues.push(clamp(growth, 0.75, 1.35));
    });
  });
  let visibilityValues = [1];
  if (profile.vis === 'recurrencia') {
    const recRows = methodology.recurrence.filter(row => row.v === input.rec);
    const possibilities = recRows.length ? recRows : methodology.recurrence;
    visibilityValues = possibilities.flatMap(row => {
      const models = row.v === 'r1' ? [1] : possible(methodology.model, input.model, 'mult');
      return models.map(mult => (1 + row.m * 0.22) * mult);
    });
  } else if (profile.vis === 'backlog') {
    visibilityValues = possible(methodology.backlog, input.backlog, 'm').map(m => 1 + m * 0.15);
  }
  const growth = interval(growthValues);
  const visibility = interval(visibilityValues);
  const concentration = interval(
    profile.conc ? possible(methodology.concentration, input.concentration, 'f') : [1]
  );
  const age = known(input.age)
    ? input.age < 2
      ? 0.93
      : input.age < 5
      ? 0.98
      : input.age < 15
      ? 1.03
      : 1.06
    : null;
  const team = methodology.team.find(row => row.v === input.team)?.f;
  if (age == null || !known(team)) return null;
  const adjustment = {
    low: clamp(growth.low * visibility.low * concentration.low * age * team, 0.55, 1.9),
    high: clamp(growth.high * visibility.high * concentration.high * age * team, 0.55, 1.9),
  };
  const size = sizeFactor(input.turnover);
  const multiple = {
    low: profile.eb[0] * size * adjustment.low,
    high: profile.eb[1] * size * adjustment.high,
  };
  const salary = marketSalary(input.turnover);
  const normalizationKeys = ['profit', 'salary', 'personal', 'extraIncome', 'extraExpense', 'rent'];
  const missingNormalization = normalizationKeys.filter(key => !known(input[key]));
  const normalizedProfit = missingNormalization.length
    ? null
    : input.profit +
      input.salary -
      salary +
      input.personal * 0.7 +
      input.extraExpense * 0.7 -
      input.extraIncome * 0.9 -
      input.rent;
  const operating =
    normalizedProfit != null && normalizedProfit > 0
      ? { low: normalizedProfit * multiple.low, high: normalizedProfit * multiple.high }
      : null;
  const missingBridge = ['debt', 'cash', 'property', 'stock'].filter(key => !known(input[key]));
  const bridge = missingBridge.length
    ? null
    : -input.debt + input.cash + input.property + input.stock;
  const equity =
    operating && bridge != null
      ? { low: operating.low + bridge, high: operating.high + bridge }
      : null;
  return {
    version: methodology.version,
    profileKey: profile.k,
    status: missingNormalization.length
      ? 'incomplete'
      : normalizedProfit <= 0
      ? 'nonpositive'
      : missingBridge.length
      ? 'partial'
      : 'complete',
    normalizedProfit,
    salary,
    multiple,
    operating,
    equity,
    bridge,
    // Not a replacement for an earnings-based result, never labeled as the operation price.
    revenueReference: {
      low: input.turnover * profile.rev[0] * adjustment.low,
      high: input.turnover * profile.rev[1] * adjustment.high,
    },
    missingNormalization,
    missingBridge,
    factors: { growth, visibility, concentration, age, team, size, adjustment },
  };
};

/** Outward rounding for display, retaining negative equity rather than hiding it at zero. */
export const roundValuationRange = range => {
  if (!range) return null;
  const round = (n, direction) => {
    const size = Math.abs(n) < 50000 ? 1000 : Math.abs(n) < 250000 ? 5000 : 10000;
    return Math[direction](n / size) * size;
  };
  return { low: round(range.low, 'floor'), high: round(range.high, 'ceil') };
};
